import { type ReactNode, useEffect, useId, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { NavLink, useLocation } from 'react-router';

import type { Me } from '../api/generated/model';
import { Button } from '../design/primitives/Button';
import { MenuIcon } from '../design/primitives/icons';
import { useLogoutAction } from '../features/auth/useSession';
import { PendencyNotice } from '../features/platform/PendencyNotice';
import styles from './AppShell.module.css';
import { LanguageSelector } from './LanguageSelector';
import { navItemsFor } from './nav';

export interface AppShellProps {
  readonly me: Me;
  readonly children: ReactNode;
}

export function AppShell({ me, children }: AppShellProps) {
  const { t } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);
  const navId = useId();
  const logout = useLogoutAction();
  const items = navItemsFor(me.role);

  const location = useLocation();
  const menuToggle = useRef<HTMLButtonElement>(null);
  const wasOpenBeforeNavigating = useRef(false);

  useEffect(() => {
    wasOpenBeforeNavigating.current = menuOpen;
  }, [menuOpen]);

  /**
   * No celular a gaveta cobre a tela pedida, e navegar precisa fecha-la.
   *
   * A casca nao e remontada a cada rota — o roteador troca so o miolo —, entao
   * `menuOpen` sobrevive a navegacao. No desktop isso nao aparece, porque a
   * lateral e fixa e `menuOpen` nao governa nada; no celular, onde a gaveta
   * empurra o conteudo para baixo da dobra, cada toque no menu deixava a pessoa
   * olhando a mesma lista, sem sinal de que a tela pedida ja tinha carregado.
   *
   * O foco volta ao botao do menu, e nao fica no ar: o link acionado estava
   * DENTRO da gaveta que acabou de sumir, e foco em elemento removido cai no
   * corpo do documento — quem navega por teclado recomecaria do topo a cada
   * destino. O botao e o vizinho mais proximo do que a pessoa acabou de fazer.
   *
   * O foco so se move quando havia gaveta aberta. Sem essa condicao, toda
   * navegacao roubaria o foco de quem esta no desktop, onde nao ha gaveta.
   */
  useEffect(() => {
    if (!wasOpenBeforeNavigating.current) return;
    setMenuOpen(false);
    menuToggle.current?.focus();
  }, [location.pathname]);

  return (
    <div className={styles.shell}>
      <a className={styles.skipLink} href="#main">
        {t('app.skipToContent')}
      </a>

      <header className={styles.header}>
        <button
          ref={menuToggle}
          type="button"
          className={styles.menuToggle}
          aria-expanded={menuOpen}
          aria-controls={navId}
          aria-label={menuOpen ? t('nav.closeMenu') : t('nav.openMenu')}
          onClick={() => setMenuOpen((open) => !open)}
        >
          <MenuIcon className={styles.menuIcon} />
        </button>

        <p className={styles.brand}>{t('app.name')}</p>
        <span className={styles.headerSpacer} />

        {/*
          `displayName` como veio, sem concatenar, cortar nem abreviar. E o unico
          campo de nome que existe, e ele ja chega decidido para este papel.
        */}
        <span className={styles.person}>{me.displayName}</span>

        <LanguageSelector persist />

        <Button onClick={() => logout.mutate()} loading={logout.isPending}>
          {logout.isPending ? t('nav.loggingOut') : t('nav.logout')}
        </Button>
      </header>

      <div className={styles.body}>
        <nav
          id={navId}
          className={[styles.sidebar, menuOpen ? styles.sidebarOpen : null]
            .filter(Boolean)
            .join(' ')}
          aria-label={t('nav.menu')}
        >
          <ul className={styles.navList}>
            {items.map((item) => (
              <li key={item.to}>
                <NavLink className={styles.navLink} to={item.to} end>
                  {t(item.labelKey)}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>

        {/*
          `tabIndex={-1}` e o que faz o atalho "ir para o conteudo" funcionar de
          verdade. Um `href="#main"` move a rolagem em todo navegador, mas so
          move o FOCO do teclado se o alvo puder receber foco: no Safari, e
          historicamente no Chrome, o foco ficava no proprio link, e o Tab
          seguinte voltava para o segundo item do cabecalho — exatamente o
          percurso que o atalho existe para pular. Com -1 o `<main>` e alvo
          programatico sem entrar na ordem de Tab, e o anel de foco global
          (`:focus-visible`) nao dispara em foco programatico, entao nao ha
          moldura aparecendo em volta da tela inteira.
        */}
        {/*
          O aviso de pendencias vem ANTES do <main>, e a posicao e a decisao: ele
          substituiu duas guardas de boot, entao precisa ser lido sem que ninguem o
          procure. Fica fora do <main> porque nao e o conteudo da tela — e a condicao
          em que a plataforma inteira esta operando.
        */}
        <PendencyNotice me={me} />

        <main id="main" tabIndex={-1} className={styles.main} aria-label={t('app.mainContent')}>
          <div className={styles.content}>{children}</div>
        </main>
      </div>
    </div>
  );
}
